import styled from 'styled-components';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { useIntl } from 'react-intl';
import CloseButton from './shared/CloseButton';
import SettingsDropDown from './SettingsDropDown';
import SettingsItem from './SettingsItem';
import { Flex } from './shared/Flex';
import media from '../style/media';
import { fontSize, fontWeight, spacing, buttonDefaults } from '../style/tokens';
import {
  useLicenseKey,
  useMeasurementDuration,
  DEFAULT_MEASUREMENT_DURATION,
  MIN_MEASUREMENT_DURATION,
  MAX_MEASUREMENT_DURATION,
} from '../hooks/useLicenseDetails';
import { healthVersion as version } from '../../package.json';
import { LocaleContext } from '../index';
import { Locale } from '../i18n';
import { Sex, SmokingStatus } from '@biosensesignal/web-sdk';
import { UserDemographics } from '../hooks/useDemographics';

const SideBar = styled(Flex) <{ reverseAnimation }>`
  position: absolute;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: 100%;
  top: 0;
  left: 0;
  background-color: #f1f4f9;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 3;
  animation-name: ${({ reverseAnimation }) =>
    reverseAnimation ? 'slide-reverse' : 'slide'};
  animation-duration: 0.3s;
  @keyframes slide {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }
  @keyframes slide-reverse {
    from {
      width: 100%;
    }
    to {
      width: 0;
    }
  }
  ${media.tablet`
    width: 400px;
    box-shadow: 2px 1px 5px rgba(0, 0, 0, 0.1);
    @keyframes slide {
      from {
        width: 0;
      }
      to {
        width: 400px;
      }
    }
    @keyframes slide-reverse {
      from {
        width: 400px;
      }
      to {
        width: 0;
      }
    }
`}
`;

const Wrapper = styled(Flex)`
  flex-direction: column;
  padding: 20px 15px;
  margin-bottom: ${spacing.xl};
  width: 100%;
  max-width: 340px;
  box-sizing: border-box;
  ${media.mobile`
    padding: 15px;
  `}
`;

const CloseWrapper = styled(Flex)`
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  margin-bottom: 15px;
`;

const CameraDropDown = styled.div`
  display: none;
  ${media.tablet`
    margin-top: 15px;
    display: block;
  `}
`;

const Title = styled.h3`
  font-style: normal;
  font-weight: ${fontWeight.normal};
  font-size: ${fontSize.base};
  line-height: 19px;
  color: #3e3c3c;
  margin-bottom: 6px;
`;

const MeasurementDurationWrapper = styled.div`
  margin-top: 15px;
`;

const LicenseStatus = styled.h3`
  margin-top: 6px;
  margin-bottom: 0;
  font-style: normal;
  font-weight: ${fontWeight.normal};
  font-size: ${fontSize.base};
  line-height: 17px;
`;

const TextBold = styled.span`
  font-weight: bold;
`;

const Version = styled.div`
  font-size: ${fontSize.base};
  color: #3e3c3c;
`;

const SettingsCard = styled.div`
  margin-top: ${spacing.xl};
  padding: ${spacing.xl};
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const ResetLinkActive = styled.a`
  margin-top: 18px;
  cursor: pointer;
  color: #0653f4;
  font-size: ${fontSize.base};
`;

const ResetLinkDisabled = styled.a`
  margin-top: 18px;
  cursor: none;
  color: #9fa2a6;
  font-size: ${fontSize.base};
`;

const HelpBlock = styled.div`
  color: #d80000;
  font-size: ${fontSize.sm};
  line-height: 14px;
  margin-top: 5px;
  margin-left: 2px;
`;

const DemographicsSection = styled.div`
  margin-top: 25px;
  padding: ${spacing.xl};
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const DemographicsTitle = styled.h3`
  font-size: ${fontSize.xl};
  font-weight: ${fontWeight.semibold};
  color: #01061b;
  margin: 0 0 ${spacing.xl} 0;
  padding-bottom: ${spacing.md};
  border-bottom: 2px solid #C0C0C0;
`;

const DemographicsField = styled.div`
  margin-bottom: ${spacing.lg};
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${fontSize.base};
  font-weight: ${fontWeight.medium};
  color: #3e3c3c;
  margin-bottom: ${spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${spacing.md};
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: ${fontSize.base};
  box-sizing: border-box;
  transition: all 0.2s;
  background-color: #fafafa;

  &:focus {
    outline: none;
    border-color: #C0C0C0;
    background-color: white;
  }

  @media (hover: hover) {
    &:hover {
      border-color: #c0c0c0;
    }
  }
`;

const SaveButton = styled.div`
  text-align: center;
  width: 90%;
  padding: 14px;
  background: ${p => p.theme.coloreBottoneInattivo || buttonDefaults.inactive};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 15px;
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  margin-top: ${spacing.xl};
  transition: background 0.2s, transform 0.2s;

  @media (hover: hover) {
    &:hover {
      background: ${p => p.theme.coloreBottoneAttivo || buttonDefaults.active};
      transform: translateY(-1px);
    }
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;


interface SettingsBarsProps {
  open: boolean;
  onClose: (params: { cameraId: string }) => void;
  cameras: MediaDeviceInfo[];
  isLicenseValid: boolean;
}

const SettingsBars = ({ open, onClose, cameras, isLicenseValid }: SettingsBarsProps) => {
  const intl = useIntl();
  const { locale, setLocale } = useContext(LocaleContext);
  const [
    processingTimeInLocalStorage,
    setProcessingTimeInLocalStorage,
  ] = useMeasurementDuration();
  const licenseKey = useLicenseKey();
  const [cameraId, setCameraId] = useState<string>('');
  const [processingTime, setProcessingTime] = useState<number>(
    processingTimeInLocalStorage,
  );
  const [isProcessingTimeValid, setIsProcessingTimeValid] = useState<boolean>(
    true,
  );
  const [isClosing, setIsClosing] = useState<boolean>();
  const [isResetClickable, setIsResetClickable] = useState<boolean>(false);

  // Demographics state
  const [demographics, setDemographics] = useState<UserDemographics | null>(null);
  const [demographicsForm, setDemographicsForm] = useState({
    sex: demographics?.sex?.toString() || Sex.MALE.toString(),
    age: demographics?.age?.toString() || '',
    weight: demographics?.weight?.toString() || '',
    height: demographics?.height?.toString() || '',
    smokingStatus: demographics?.smokingStatus?.toString() || SmokingStatus.NON_SMOKER.toString(),
  });

  const languageOptions = [
    { value: 'en', name: 'English' },
    { value: 'de', name: 'Deutsch' },
    { value: 'es', name: 'Español' },
    { value: 'fr', name: 'Français' },
    { value: 'it', name: 'Italiano' },
    { value: 'ar', name: 'العربية' },
    { value: 'lv', name: 'Latviešu' },
    { value: 'pt', name: 'Português' },
    { value: 'ja', name: '日本語' },
  ];

  const mapCamerasToDropDown = useCallback(
    (cameras) =>
      cameras?.map(({ deviceId, label }) => ({ value: deviceId, name: label })),
    [],
  );

  const handleCameraSelected = useCallback((cameraId) => {
    setCameraId(cameraId);
  }, []);

  const handleLanguageSelected = useCallback((languageCode: string) => {
    setLocale(languageCode as Locale);
  }, [setLocale]);

  const handleClose = useCallback(() => {
    if (!isProcessingTimeValid) {
      return;
    }
    setIsClosing(true);
    setTimeout(() => {
      onClose({ cameraId });
      setIsClosing(false);
    }, 200);
  }, [cameraId, isProcessingTimeValid]);

  const onProcessingTimeChange = useCallback((event) => {
    const processingTime = event.target.value;
    setProcessingTime(processingTime);
    setIsProcessingTimeValid(
      processingTime >= MIN_MEASUREMENT_DURATION &&
      processingTime <= MAX_MEASUREMENT_DURATION,
    );
    setIsResetClickable(true);
  }, []);

  const onProcessingTimeBlur = useCallback((event) => {
    setProcessingTimeInLocalStorage(event.target.value);
  }, []);


  const onResetSettingsValues = useCallback(() => {
    setProcessingTime(DEFAULT_MEASUREMENT_DURATION);
    setProcessingTimeInLocalStorage(DEFAULT_MEASUREMENT_DURATION);
    setIsProcessingTimeValid(true);
    setIsResetClickable(false);
  }, []);

  const handleDemographicsChange = useCallback((field: string, value: string) => {
    setDemographicsForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSaveDemographics = useCallback(() => {
    const ageNum = parseInt(demographicsForm.age);
    const weightNum = parseFloat(demographicsForm.weight);
    const heightNum = parseFloat(demographicsForm.height);

    if (!ageNum || !weightNum || !heightNum) {
      alert(intl.formatMessage({ id: 'validation.fillAllFields' }));
      return;
    }

    if (ageNum < 18 || ageNum > 120) {
      alert(intl.formatMessage({ id: 'validation.ageRange' }));
      return;
    }

    if (weightNum < 30 || weightNum > 300) {
      alert(intl.formatMessage({ id: 'validation.weightRange' }));
      return;
    }

    if (heightNum < 100 || heightNum > 250) {
      alert(intl.formatMessage({ id: 'validation.heightRange' }));
      return;
    }

    const updatedDemographics: UserDemographics = {
      sex: parseInt(demographicsForm.sex) as Sex,
      age: ageNum,
      weight: weightNum,
      height: heightNum,
      smokingStatus: parseInt(demographicsForm.smokingStatus) as SmokingStatus,
    };

    setDemographics(updatedDemographics);
    alert(intl.formatMessage({ id: 'form.save' }) + ' ' + intl.formatMessage({ id: 'settings.valid' }));
  }, [demographicsForm, intl]);

  useEffect(() => {
    cameras?.length && setCameraId(cameras[0].deviceId);
  }, [cameras]);

  useEffect(() => {
    if (demographics) {
      setDemographicsForm({
        sex: demographics.sex.toString(),
        age: demographics.age.toString(),
        weight: demographics.weight.toString(),
        height: demographics.height.toString(),
        smokingStatus: (demographics.smokingStatus ?? SmokingStatus.NON_SMOKER).toString(),
      });
    }
  }, [demographics]);

  return (
    <div id="settingsBars">
      {open && (
        <SideBar reverseAnimation={!!isClosing}>
          <Wrapper>
            <CloseWrapper>
              <CloseButton onClick={handleClose} />
            </CloseWrapper>
            {/* <Version>{intl.formatMessage({ id: 'settings.version' })}: {version.replace('-', '(').concat(')')}</Version> */}

            <SettingsCard>
              <Title>{intl.formatMessage({ id: 'settings.language' })}</Title>
              <SettingsDropDown
                onSelect={handleLanguageSelected}
                options={languageOptions}
                selectedValue={locale}
              />

              <CameraDropDown>
                <Title style={{ marginTop: '20px' }}>{intl.formatMessage({ id: 'settings.camera' })}</Title>
                <SettingsDropDown
                  onSelect={handleCameraSelected}
                  options={mapCamerasToDropDown(cameras)}
                />
              </CameraDropDown>
            </SettingsCard>

            {/* KNOCKING OUT BASE DATA THAT DISPLAYS LICENSE
            <SettingsItem
              title={intl.formatMessage({ id: 'settings.licenseKey' })}
              type={'password'}
              value={licenseKey}
              onChange={onLicenseKeyChange}
              onBlur={onLicenseKeyBlur}
              isValid={isLicenseValid}
            />
             <LicenseStatus>
              {intl.formatMessage({ id: 'settings.licenseStatus' })}:
              <TextBold>
                {licenseKey && isLicenseValid ? ` ${intl.formatMessage({ id: 'settings.valid' })}` : ` ${intl.formatMessage({ id: 'settings.invalid' })}`}
              </TextBold>
            </LicenseStatus>
             <MeasurementDurationWrapper>
              <SettingsItem
                title={intl.formatMessage({ id: 'settings.measurementDuration' })}
                type={'number'}
                value={processingTime}
                onChange={onProcessingTimeChange}
                onBlur={onProcessingTimeBlur}
                isValid={isProcessingTimeValid}
              />
            </MeasurementDurationWrapper> */}
            {/* ALSO KNOCIKING THIS OUT AS WE DON'T WANT TO RESET LICENSE */}
            {/* {!isProcessingTimeValid && (
              <HelpBlock>Valid range: 20-180</HelpBlock>
            )}
            {isResetClickable ? (
              <ResetLinkActive onClick={onResetSettingsValues}>
                {intl.formatMessage({ id: 'settings.resetSettings' })}
              </ResetLinkActive>
            ) : (
              <ResetLinkDisabled onClick={onResetSettingsValues}>
                {intl.formatMessage({ id: 'settings.resetSettings' })}
              </ResetLinkDisabled>
            )} */}

            <DemographicsSection>
              <DemographicsTitle>{intl.formatMessage({ id: 'settings.userInformation' })}</DemographicsTitle>

              <DemographicsField>
                <FieldLabel>{intl.formatMessage({ id: 'form.sex' })}</FieldLabel>
                <SettingsDropDown
                  onSelect={(value) => handleDemographicsChange('sex', value)}
                  options={[
                    { value: Sex.MALE.toString(), name: intl.formatMessage({ id: 'form.male' }) },
                    { value: Sex.FEMALE.toString(), name: intl.formatMessage({ id: 'form.female' }) },
                  ]}
                  selectedValue={demographicsForm.sex}
                />
              </DemographicsField>

              <DemographicsField>
                <FieldLabel>{intl.formatMessage({ id: 'form.age' })}</FieldLabel>
                <Input
                  type="number"
                  min="18"
                  max="120"
                  value={demographicsForm.age}
                  onChange={(e) => handleDemographicsChange('age', e.target.value)}
                  placeholder={intl.formatMessage({ id: 'form.agePlaceholder' })}
                />
              </DemographicsField>

              <DemographicsField>
                <FieldLabel>{intl.formatMessage({ id: 'form.weight' })}</FieldLabel>
                <Input
                  type="number"
                  min="30"
                  max="300"
                  step="0.1"
                  value={demographicsForm.weight}
                  onChange={(e) => handleDemographicsChange('weight', e.target.value)}
                  placeholder={intl.formatMessage({ id: 'form.weightPlaceholder' })}
                />
              </DemographicsField>

              <DemographicsField>
                <FieldLabel>{intl.formatMessage({ id: 'form.height' })}</FieldLabel>
                <Input
                  type="number"
                  min="100"
                  max="250"
                  value={demographicsForm.height}
                  onChange={(e) => handleDemographicsChange('height', e.target.value)}
                  placeholder={intl.formatMessage({ id: 'form.heightPlaceholder' })}
                />
              </DemographicsField>

              <DemographicsField>
                <FieldLabel>{intl.formatMessage({ id: 'form.smokingStatus' })}</FieldLabel>
                <SettingsDropDown
                  onSelect={(value) => handleDemographicsChange('smokingStatus', value)}
                  options={[
                    { value: SmokingStatus.NON_SMOKER.toString(), name: intl.formatMessage({ id: 'form.nonSmoker' }) },
                    { value: SmokingStatus.SMOKER.toString(), name: intl.formatMessage({ id: 'form.smoker' }) },
                  ]}
                  selectedValue={demographicsForm.smokingStatus}
                />
              </DemographicsField>

              <SaveButton onClick={handleSaveDemographics}>
                {intl.formatMessage({ id: 'form.save' })}
              </SaveButton>
            </DemographicsSection>
          </Wrapper>
        </SideBar>
      )}
    </div>
  );
};

export default SettingsBars;
